import { Holding } from "../../../services/holdings";
import { InstrumentSummary } from "../../../services/instruments";
import { PriceOverrideRequest, PriceOverrideTarget } from "./types";

// A reconciled rename moves the holding to its successor, but an earlier date still prices the
// predecessor — offering `instrument` for a pre-rename date would save happily and repair nothing.
const listingOn = (holding: Holding, asOf: string): InstrumentSummary => {
  const listing = holding.priorListings.find(
    (candidate) => candidate.fromDate <= asOf && asOf < candidate.untilDate,
  );
  return listing?.instrument ?? holding.instrument;
};

// Two positions in the same ticker are two candidates but one decision — pricing either is the same write.
const distinctById = (
  instruments: InstrumentSummary[],
): InstrumentSummary[] => [
  ...new Map(
    instruments.map((instrument) => [instrument.id, instrument]),
  ).values(),
];

// The chart's blocked state names a date, not an instrument, so candidates come from the ledger.
// With several candidates the panel makes the user choose — a wrong-instrument price is a silent error.
export const priceOverrideTargetsForBlockedDate = (
  holdings: Holding[],
  asOf: string,
): PriceOverrideTarget => {
  const candidates = distinctById(
    holdings
      .filter((holding) => holding.openedAt <= asOf)
      .map((holding) => listingOn(holding, asOf)),
  );
  const fallback = holdings.map((holding) => holding.instrument);
  const instruments: InstrumentSummary[] =
    candidates.length > 0 ? candidates : fallback;

  return {
    initialInstrument: candidates.length === 1 ? candidates[0] : null,
    instruments,
  };
};

const NO_TARGET: PriceOverrideTarget = {
  initialInstrument: null,
  instruments: [],
};

const priceOverrideTargetsForHolding = (
  holding: Holding,
): PriceOverrideTarget => {
  const instruments = [
    ...holding.priorListings.map((listing) => listing.instrument),
    holding.instrument,
  ];

  // Once renamed, the date field determines which listing a private price belongs to — make the
  // user choose instead of silently storing it on today's ticker.
  return {
    initialInstrument: instruments.length === 1 ? holding.instrument : null,
    instruments,
  };
};

const priceOverrideTargetsForHoldings = (
  holdings: Holding[],
): PriceOverrideTarget => {
  const instruments = distinctById(
    holdings.flatMap(
      (holding) => priceOverrideTargetsForHolding(holding).instruments,
    ),
  );
  return {
    initialInstrument: instruments.length === 1 ? instruments[0] : null,
    instruments,
  };
};

// Resolved on every render rather than captured on open — the chart and holdings are separate
// queries, so a target frozen too early could leave nothing to select while the panel stayed open.
export const priceOverrideTargetFor = (
  request: PriceOverrideRequest | null,
  holdings: Holding[],
): PriceOverrideTarget => {
  if (request === null) {
    return NO_TARGET;
  }
  if (request.kind === "holding") {
    return priceOverrideTargetsForHolding(request.holding);
  }
  if (request.kind === "holdings") {
    return priceOverrideTargetsForHoldings(request.holdings);
  }
  if (request.kind === "instrument") {
    return {
      initialInstrument: request.instrument,
      instruments: [request.instrument],
    };
  }
  return priceOverrideTargetsForBlockedDate(holdings, request.asOf);
};
